import { Grid, GridItem, Heading, Stack, Text } from '@chakra-ui/react';
import { LineItem } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { prettyCurrency } from '@pelicargo/utils';

import { Quote } from '../../hooks/quotes/useQuoteHelper';
import { QuoteVariantWithLineItemsPossibleInland, useQuoteVariantHelper } from '../../hooks/useQuoteVariantHelper';
import { CopyLineItemsButton } from '../CopyLineItemsButton';
import { LineItemUI } from './LineItem';
import { LineItemHeader } from './LineItemHeader';

export const QuoteVariantUI = ({
  quote,
  quoteVariant,
  ...options
}: {
  quote: Quote;
  quoteVariant: QuoteVariantWithLineItemsPossibleInland;

  showTitle?: boolean;
  showTotal?: boolean;
  showMarkupBreakdown?: boolean;
  showVariantTotal?: boolean;
  showCopy?: boolean;
}) => {
  const {
    hasInlandService,

    totalCost,
    allInVariableRate,

    airFreightAllInVariableRate,
    airFreightTotal,

    inlandAllInVariableRate,
    inlandTotal,

    airlineMarkupTotal,
    inlandMarkupTotal,
    totalCostWithoutMarkup,

    extraInlandMarkups,
  } = useQuoteVariantHelper(quoteVariant, quote);
  const renderLineItem = (lineItem: LineItem) => <LineItemUI key={lineItem.id} lineItem={lineItem} />;

  const showTotal = options?.showTotal ?? true;
  const showTitle = options?.showTitle ?? true;
  const showCopy = options?.showCopy ?? true;
  const showVariantTotal = options?.showVariantTotal ?? hasInlandService;
  const showMarkupBreakdown = options?.showMarkupBreakdown ?? false;

  const excludeLineItemIds = extraInlandMarkups.map((item) => item.id);
  const inlandServiceLineItems =
    quoteVariant?.inland_service_quote_variant?.line_items?.filter(
      (lineItem) => !excludeLineItemIds.includes(lineItem.id),
    ) || [];

  return (
    <Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" role="group">
        <Show if={showTitle}>
          <Text textStyle="label" color="text.subtle">
            {quoteVariant?.name}
          </Text>
        </Show>
        <Show if={showCopy}>
          <CopyLineItemsButton quoteVariant={quoteVariant} quote={quote} />
        </Show>
      </Stack>
      <Grid templateColumns="repeat(5, 1fr)" gap={0} w="full">
        <Show if={showVariantTotal}>
          <LineItemHeader
            label="Airline Service"
            perUnitValue={airFreightAllInVariableRate}
            totalCost={airFreightTotal}
          />
        </Show>
        {quoteVariant?.line_items?.map(renderLineItem)}

        <Show if={showVariantTotal}>
          <LineItemHeader
            mt={1}
            label="Inland Service"
            perUnitValue={inlandAllInVariableRate}
            totalCost={inlandTotal}
          />
          {inlandServiceLineItems?.map(renderLineItem)}
        </Show>

        <Show if={showTotal}>
          <GridItem colSpan={5} borderTopWidth={1} my={2} />
          <GridItem colSpan={3} pl="4">
            <Heading variant="h6" w="auto">
              Total
            </Heading>
          </GridItem>
          <GridItem textAlign="right">
            <Show if={allInVariableRate > 0}>
              <Heading variant="h6" textAlign="right">
                {prettyCurrency(allInVariableRate)}/kg
              </Heading>
            </Show>
          </GridItem>
          <GridItem textAlign="right" pr="4">
            <Show if={totalCost > 0}>
              <Heading variant="h6" textAlign="right">
                {totalCost ? prettyCurrency(totalCost) : ''}
              </Heading>
            </Show>
          </GridItem>
        </Show>
        <Show if={showMarkupBreakdown}>
          <GridItem colSpan={5} borderTopWidth={1} my={2} />
          <GridItem colSpan={3} pl="4">
            <Heading variant="h6" w="auto">
              Buy Rate
            </Heading>
          </GridItem>
          <GridItem textAlign="right" pr="4" colSpan={2}>
            <Show if={totalCostWithoutMarkup > 0}>
              <Heading variant="h6" textAlign="right">
                {totalCostWithoutMarkup ? prettyCurrency(totalCostWithoutMarkup) : ''}
              </Heading>
            </Show>
          </GridItem>
          <Show if={airlineMarkupTotal > 0}>
            <GridItem textAlign="left" pl="4" colSpan={2}>
              <Text textStyle="bodySmallRegular">Airline Markup</Text>
            </GridItem>
            <GridItem textAlign="right" pr="4" colSpan={3}>
              <Text textStyle="bodySmallRegular">+{prettyCurrency(airlineMarkupTotal)}</Text>
            </GridItem>
          </Show>
          <Show if={inlandMarkupTotal > 0}>
            <GridItem textAlign="left" pl="4" colSpan={2}>
              <Text textStyle="bodySmallRegular">Inland Markup</Text>
            </GridItem>
            <GridItem textAlign="right" pr="4" colSpan={3}>
              <Text textStyle="bodySmallRegular">+{prettyCurrency(inlandMarkupTotal)}</Text>
            </GridItem>
          </Show>
          <Show if={extraInlandMarkups.length > 0}>
            {extraInlandMarkups.map((item, index) => (
              <>
                <GridItem textAlign="left" pl="4" colSpan={2}>
                  <Text textStyle="bodySmallRegular">{item.label}</Text>
                </GridItem>
                <GridItem key={index} textAlign="right" pr="4" colSpan={3}>
                  <Text textStyle="bodySmallRegular">+{prettyCurrency(item.cost)}</Text>
                </GridItem>
              </>
            ))}
          </Show>
          <GridItem colSpan={5} borderTopWidth={1} my={2} />
          <GridItem colSpan={3} pl="4">
            <Heading variant="h6" w="auto">
              Sell Rate
            </Heading>
          </GridItem>
          <GridItem textAlign="right">
            <Show if={allInVariableRate > 0}>
              <Heading variant="h6" textAlign="right">
                {prettyCurrency(allInVariableRate)}/kg
              </Heading>
            </Show>
          </GridItem>
          <GridItem textAlign="right" pr="4">
            <Show if={totalCost > 0}>
              <Heading variant="h6" textAlign="right">
                {totalCost ? prettyCurrency(totalCost) : ''}
              </Heading>
            </Show>
          </GridItem>
        </Show>
      </Grid>
    </Stack>
  );
};
